// src/components/keepers/KeeperPositionModal.js
import React, { useState, useMemo } from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

// Composite-slot eligibility helper. CI accepts 1B/3B, MI accepts 2B/SS,
// all other slots match a literal position in the player's effective
// eligibility list. Exported as a named export per Bible §14 frontend
// testing convention so unit tests can target the helper directly
// without mock circularity or inline reimplementation.
export const isEligibleForPosition = (positionKey, effective) => {
  if (positionKey === 'CI') return effective.some(p => ['1B', '3B'].includes(p));
  if (positionKey === 'MI') return effective.some(p => ['2B', 'SS'].includes(p));
  return effective.includes(positionKey);
};

/**
 * Keeper Position Modal
 * Simple modal for assigning a single keeper to a roster position
 *
 * Much simpler than BulkPositionAssignmentModal - handles ONE player at a time
 */
const KeeperPositionModal = ({
  player,
  league,
  currentAssignments, // { player_id: roster_position }
  maxKeepers,
  selectedCount,
  onConfirm,
  onCancel,
  isVisible = false
}) => {
  const [selectedPosition, setSelectedPosition] = useState('');
  const [error, setError] = useState('');

  // Get available positions for this player
  const availablePositions = useMemo(() => {
    if (!player || !league?.roster_settings?.position_requirements) return [];

    const options = [];
    const posReqs = league.roster_settings.position_requirements;
    
    // Use effective_eligibility from the canonical player object
    // (injected by _inject_effective_eligibility in keeper_service / keeper_projection_service)
    const effective = player.league_context?.roster?.effective_eligibility;

    // Check each position requirement
    Object.entries(posReqs).forEach(([positionKey, config]) => {
      const slotCount = config?.slots || 0;

      // Check if player is eligible for this position type
      const isEligible = isEligibleForPosition(positionKey, effective);
      
      if (isEligible) {
        // Create option for each slot
        for (let i = 0; i < slotCount; i++) {
          const slotId = `${positionKey}_${i}`;
          
          // Check if this slot is already taken by ANOTHER player
          const currentPlayerId = player.ids?.mlb;
          const occupyingPlayerId = Object.entries(currentAssignments).find(
            ([pid, assignment]) => assignment.roster_position === slotId && parseInt(pid) !== currentPlayerId
          )?.[0];
          
          const isAvailable = !occupyingPlayerId;
          
          options.push({
            value: slotId,
            label: `${positionKey.toUpperCase()} #${i + 1}`,
            type: 'active',
            available: isAvailable,
            occupiedBy: occupyingPlayerId ? `Player #${occupyingPlayerId}` : null
          });
        }
      }
    });
    
    return options.sort((a, b) => {
      // Available first
      if (a.available !== b.available) return a.available ? -1 : 1;
      // Then alphabetically
      return a.label.localeCompare(b.label);
    });
  }, [player, league, currentAssignments]);

  // Check if at max keepers
  const atMaxKeepers = selectedCount >= maxKeepers;

  const handleConfirm = () => {
    if (!selectedPosition) {
      setError('Please select a position');
      return;
    }

    // Find the option to check availability
    const option = availablePositions.find(o => o.value === selectedPosition);
    if (!option?.available) {
      setError(`Position ${option?.label} is already occupied`);
      return;
    }

    if (atMaxKeepers) {
      setError(`Cannot exceed ${maxKeepers} keepers`);
      return;
    }

    onConfirm(player.ids?.mlb, selectedPosition);
  };

  if (!isVisible || !player) return null;

  // TODO: PlayerLink — playerName here is the modal header for the player being kept; not appropriate to open another modal on top.
  const playerName = player.info?.full_name ||
                    `${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim() ||
                    `Player #${player.ids?.mlb}`;

  const playerPosition = player.info?.position || 'UTIL';
  const playerTeam = player.info?.mlb_team || '-';

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto"
      style={{ paddingTop: '10vh' }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={onCancel}
      />
      
      {/* Modal */}
      <div className={`${dynastyTheme.components.card.base} w-full max-w-md relative z-10 my-8`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-700/30">
          <div>
            <h2 className={`${dynastyTheme.components.heading.h2} mb-0`}>
              Assign Keeper Position
            </h2>
            <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-1`}>
              Where should this player go in your keeper roster?
            </p>
          </div>
          <button
            onClick={onCancel}
            className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-600/50 ${dynastyTheme.classes.transition}`}
          >
            <X className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
          </button>
        </div>

        {/* Player Info */}
        <div className={`p-6 border-b border-neutral-700/30 ${dynastyTheme.classes.bg.neutral}/10`}>
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`${dynastyTheme.classes.text.white} font-semibold text-lg`}>
              {playerName}
            </h3>
            <span className={dynastyTheme.components.badge.neutral}>
              {playerPosition}
            </span>
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              {playerTeam}
            </span>
          </div>
          
          {/* Show eligible positions */}
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            Eligible: {availablePositions.length > 0 
              ? (() => {
                  // Get unique position types (e.g., "C", "OF", "SS")
                  const posTypes = [...new Set(availablePositions.map(o => o.label.split(' #')[0]))];
                  return posTypes.join(', ');
                })()
              : 'No positions available'
            }
          </div>
        </div>

        {/* Position Selection */}
        <div className="p-6">
          <label className={`block text-sm font-medium ${dynastyTheme.classes.text.white} mb-2`}>
            Select Roster Position
          </label>
          
          <select
            value={selectedPosition}
            onChange={(e) => {
              setSelectedPosition(e.target.value);
              setError('');
            }}
            className={`${dynastyTheme.components.input} w-full ${
              error ? 'border-red-500/50 bg-red-500/10' : ''
            }`}
          >
            <option value="">-- Choose Position --</option>
            {availablePositions.map(option => (
              <option 
                key={option.value} 
                value={option.value}
                disabled={!option.available}
              >
                {option.label}
                {!option.available && ` - Occupied by ${option.occupiedBy}`}
              </option>
            ))}
          </select>

          {/* Error Display */}
          {error && (
            <div className={`mt-3 p-3 rounded-lg ${dynastyTheme.classes.bg.error}/10 flex items-center gap-2`}>
              <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.error}`} />
              <span className={`text-sm ${dynastyTheme.classes.text.error}`}>{error}</span>
            </div>
          )}

          {/* Capacity Warning */}
          {atMaxKeepers && (
            <div className={`mt-3 p-3 rounded-lg ${dynastyTheme.classes.bg.warning}/10 flex items-center gap-2`}>
              <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} />
              <span className={`text-sm ${dynastyTheme.classes.text.warning}`}>
                At maximum keepers ({selectedCount}/{maxKeepers})
              </span>
            </div>
          )}

          {/* No Positions Available */}
          {availablePositions.length === 0 && (
            <div className={`mt-3 p-3 rounded-lg ${dynastyTheme.classes.bg.error}/10 flex items-center gap-2`}>
              <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.error}`} />
              <span className={`text-sm ${dynastyTheme.classes.text.error}`}>
                No eligible positions available for this player
              </span>
            </div>
          )}
        </div>

        {/* Keeper Count Display */}
        <div className={`px-6 pb-4 border-t border-neutral-700/30`}>
          <div className="pt-4 flex items-center justify-between">
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Keepers Selected:
            </span>
            <span className={`text-sm font-bold ${
              selectedCount >= maxKeepers 
                ? dynastyTheme.classes.text.warning 
                : dynastyTheme.classes.text.primary
            }`}>
              {selectedCount}/{maxKeepers}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-6 border-t border-neutral-700/30">
          <button
            onClick={onCancel}
            className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} flex-1`}
          >
            Cancel
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={!selectedPosition || atMaxKeepers || availablePositions.length === 0}
            className={`
              ${!selectedPosition || atMaxKeepers || availablePositions.length === 0
                ? 'px-5 py-2.5 text-base bg-neutral-700/50 text-neutral-500 cursor-not-allowed rounded-md' 
                : dynastyTheme.utils.getComponent('button', 'primary', 'md')
              } 
              flex items-center gap-2 flex-1
            `}
          >
            <CheckCircle className="w-4 h-4" />
            Confirm Position
          </button>
        </div>
      </div>
    </div>
  );
};

export default KeeperPositionModal;
